import clsx from 'clsx';
import type { ReactNode } from 'react';
import { ButtonField, Icon } from '@wk/components-v3-react16';
import styles from './DropZone.module.scss';

export interface DropZoneLabelOptions {
  disableBackgroundColor?: boolean;
  hideIcons?: boolean;
  hideText?: boolean;
  isFailback?: boolean;
  isGlobalFailback?: boolean;
  isNested?: boolean;
  isNextStepGroup?: boolean;
  isOver?: boolean;
  isParallelStep?: boolean;
  isPreviousStepGroup?: boolean;
}

export function getDropZoneLabel({
  isFailback = false,
  isGlobalFailback = false,
  isNested = false,
  isNextStepGroup = false,
  isOver = false,
  isParallelStep = false,
  isPreviousStepGroup = false,
  hideIcons = false,
  hideText = false,
  disableBackgroundColor = false,
}: DropZoneLabelOptions): ReactNode {
  const getIcon = (iconName: string) =>
    hideIcons ? null : <Icon name={iconName} />;
  const getText = (text: string) => (hideText ? '' : text);

  const formatLabel = (iconName: string, text: string) => {
    const icon = getIcon(iconName);
    const textPart = getText(text);

    if (!icon && !textPart) {
      return '';
    }
    if (!icon) {
      return (
        <ButtonField
          type="primary"
          flowClass={clsx({
            [styles.btn]: !disableBackgroundColor,
            [styles.btnBlack]: disableBackgroundColor,
          })}
        >
          <button type="button">{textPart}</button>
        </ButtonField>
      );
    }
    if (!textPart) {
      return (
        <ButtonField
          type="primary"
          flowClass={clsx({
            [styles.btn]: !disableBackgroundColor,
            [styles.btnBlack]: disableBackgroundColor,
          })}
        >
          <button type="button">{icon}</button>
        </ButtonField>
      );
    }

    return (
      <ButtonField
        type="primary"
        flowClass={clsx({
          [styles.btn]: !disableBackgroundColor,
          [styles.btnBlack]: disableBackgroundColor,
        })}
      >
        <button type="button">
          {icon} {textPart}
        </button>
      </ButtonField>
    );
  };
  const previousStep = 'Create previous step group';
  if (isGlobalFailback) {
    if (isPreviousStepGroup) {
      return formatLabel('wk-icon-filled-caution', previousStep);
    }
    if (isPreviousStepGroup) {
      return formatLabel('wk-icon-arrow-up', previousStep);
    }

    if (isOver) {
      return formatLabel('wk-icon-check', 'Release to drop');
    }

    if (isParallelStep) {
      return formatLabel('wk-icon-plus', 'Parallel step');
    }

    if (isFailback) {
      return formatLabel('wk-icon-caution', 'Error handler');
    }

    if (isNested) {
      return formatLabel('wk-icon-hierarchy', 'Nested workflow');
    }

    if (isNextStepGroup) {
      return formatLabel('wk-icon-arrow-down', 'Next step group');
    }
    return formatLabel('wk-icon-filled-caution', 'Global Error handler');
  }

  if (isPreviousStepGroup) {
    return formatLabel('wk-icon-arrow-up', previousStep);
  }

  if (isOver) {
    return formatLabel('wk-icon-check', 'Release to drop');
  }

  if (isParallelStep) {
    return formatLabel('wk-icon-plus', 'Parallel step');
  }

  if (isFailback) {
    return formatLabel('wk-icon-caution', 'Error handler');
  }

  if (isNested) {
    return formatLabel('wk-icon-hierarchy', 'Nested workflow');
  }

  if (isNextStepGroup) {
    return formatLabel('wk-icon-arrow-down', 'Next step group');
  }

  return 'Check logic';
}

export function getDropZoneBackgroundColor({
  isFailback = false,
  isNested = false,
  isNextStepGroup = false,
  isOver = false,
  isParallelStep = false,
}: DropZoneLabelOptions): string {
  if (isOver) {
    return '#648d18'; // WK Green shade1 - success/confirmation (dark green, white text readable)
  }
  if (isParallelStep) {
    return '#005b92'; // WK Blue shade1 - parallel/simultaneous action (dark blue, white text readable)
  }
  if (isFailback) {
    return '#af6b00'; // WK Orange shade1 - warning/error handling (dark orange, white text readable)
  }

  if (isNested) {
    return '#007261'; // WK deprecated secondary green shade1 - distinct workflow type (dark teal, white text readable)
  }

  if (isNextStepGroup) {
    return '#007ac3'; // WK Blue base - progression/next action (bright blue, white text readable)
  }

  return '#757575'; // WK Gray tint1 - default/neutral (medium gray, white text readable)
}
